import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import RootApp from './App';
import reportWebVitals from './reportWebVitals';
import { store } from './Reducers';
import { Provider } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import capacitorStorage, { CapacitorCache } from 'Utils/CapacitorStorage';
import { Amplify } from 'aws-amplify';
import { awsRum } from 'Utils/AwsRum';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { setupAxiosInterceptors } from 'Utils/axiosInterceptor';
import { AudioRecognitionProvider } from 'AudioRecognition';
import { StartupErrorBoundary, StartupLoadingScreen } from './StartupFallbacks';

const region = process.env.REACT_APP_COGNITO_AWS_REGION;
const cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
// const cognitoGuestRoleArn = process.env.REACT_APP_COGNITO_GUEST_ROLE_ARN;
const awsIdentityPoolId = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
const awsPinpointApplicationId = process.env.REACT_APP_PINPOINT_APPLICATION_ID;

const theme = createTheme({
  typography: {
    button: {
      textTransform: 'none',
    },
  },
});

const rootNode = document.getElementById('root');

const persistor = persistStore(store);

const awsConfig = {
  Auth: {
    Cognito: {
      region: region!,
      userPoolId: cognitoUserPoolId!,
      userPoolClientId: cognitoClientId!,
      identityPoolId: awsIdentityPoolId!,
      // do not add oauth config. OAuth is now handled by react-oidc-context
    },
  },
  Analytics: {
    AWSPinpoint: {
      appId: awsPinpointApplicationId,
      region: region,
    },
  },
};
Amplify.configure(awsConfig);
// no need to use the same sharded cache for amplify - it shards itself
const amplifyCache = new CapacitorCache();
cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifyCache);

const STARTUP_TASK_TIMEOUT_MS = 5000;
const STARTUP_PERSIST_TIMEOUT_MS = 5000;

const startupNow = () => Math.round(performance.now());

async function runStartupTask<T>(label: string, task: () => Promise<T>): Promise<T | undefined> {
  const startedAt = startupNow();
  console.info(`[startup] ${label} started`);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(
        `[startup] ${label} timed out after ${startupNow() - startedAt}ms; continuing startup.`,
      );
      resolve(undefined);
    }, STARTUP_TASK_TIMEOUT_MS);
  });

  try {
    const result = await Promise.race([task(), timeout]);
    console.info(
      `[startup] ${label} finished in ${startupNow() - startedAt}ms`,
      result === undefined ? { result: 'undefined' } : { result: 'defined' },
    );
    return result;
  } catch (error) {
    console.error(
      `[startup] ${label} failed after ${startupNow() - startedAt}ms; continuing startup.`,
      error,
    );
    return undefined;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

function installStartupErrorLogging() {
  window.addEventListener('error', (event) => {
    console.error('Uncaught startup error:', event.error || event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled startup promise rejection:', event.reason);
  });
}

function TimedPersistGate({ children }: { children: React.ReactNode }) {
  const [forceOpen, setForceOpen] = React.useState(false);
  const bootstrappedRef = React.useRef(false);

  React.useEffect(() => {
    // Watchdog, not strategy: rehydration is a local read and should finish in
    // milliseconds. This firing means storage is wedged — surface it to RUM
    // and fail open so the app still mounts.
    const timeoutId = setTimeout(() => {
      if (bootstrappedRef.current) return;
      const error = new Error(
        `[startup] PersistGate still not bootstrapped after ${STARTUP_PERSIST_TIMEOUT_MS}ms; rendering without persisted state.`,
      );
      console.warn(error.message);
      awsRum?.recordError(error);
      setForceOpen(true);
    }, STARTUP_PERSIST_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <PersistGate persistor={persistor}>
      {(bootstrapped: boolean) => {
        bootstrappedRef.current = bootstrapped;
        return bootstrapped || forceOpen ? <>{children}</> : <StartupLoadingScreen />;
      }}
    </PersistGate>
  );
}

function renderApp() {
  if (!rootNode) {
    throw new Error('Root node not found');
  }

  console.info(`[startup] renderApp called at ${startupNow()}ms`);
  const root = createRoot(rootNode!);
  root.render(
    <Provider store={store}>
      <TimedPersistGate>
        <StartupErrorBoundary>
          <ThemeProvider theme={theme}>
            <AudioRecognitionProvider>
              <RootApp store={store} />
            </AudioRecognitionProvider>
          </ThemeProvider>
        </StartupErrorBoundary>
      </TimedPersistGate>
    </Provider>,
  );

  reportWebVitals();
}

async function bootstrap() {
  installStartupErrorLogging();
  // Setup axios interceptors to handle 401 errors and token refresh.
  setupAxiosInterceptors();
  console.info(`[startup] bootstrap started at ${startupNow()}ms`);

  // Session resolution happens once, inside AuthWrapperProvider via
  // authService.bootstrapSession() — routing is gated on its result, so no
  // pre-render lookup is needed here.
  // Warm the Capacitor storage cache in the background. Correctness does not depend on
  // this finishing before render because CapacitorStorage.getItem is pull-through.
  void runStartupTask('Capacitor storage initialization', () => capacitorStorage.init());
  renderApp();
}

bootstrap().catch((error) => {
  console.error('Fatal startup failure:', error);
  if (rootNode) {
    createRoot(rootNode).render(<StartupLoadingScreen />);
  }
});
